import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config/api';
import './SchedulerTab.css';

/*
 * SchedulerTab — 📅 Scheduler & Drafts. Google Sheet(주차 스케줄 + 초안 링크)를 대체한다.
 * D-0 기준으로 생성된 주차 그리드를 보여주고, 각 초안 슬롯(day/slot)에 드래그드롭/파일셀렉으로
 * 영상을 올린다(R2, 재업로드=새 버전 v1→v2…). Retainer/Bonus payout은 읽기전용 칼럼으로 미러.
 * 초안 업로드는 크리에이터 셀프서브(별도 admin 게이트 없음).
 */

interface DraftVersion { version: number; url: string | null; filename: string | null; uploadedAt: string; }
interface DraftSlot { day: number; slot: number; versions: DraftVersion[]; current: DraftVersion | null; }
interface Evaluation { type: 'mid' | 'final'; label: string; }
interface PostLink { code: string; playCount: number; takenAt: string; isPov?: boolean; }
interface DelayMarker {
  fromDay: number; toDay: number; expectedCum: number; actualCum: number; delayDays: number; atWeek: number;
}
interface WeekCriteria { week: number; kind: 'warmup_report' | 'posts'; target: number; cumTarget?: number; label: string; }
interface WeekProgress { current: number; target: number; kind: 'reports' | 'posts'; totalPosted?: number; cumTarget?: number; }
interface ScheduleRow {
  week: number; day: number; phase: 'create' | 'warmup' | 'ramp' | 'full';
  dailyGoal: string; uploadCount: number; cumulativeTotal: number; date: string | null; isToday: boolean;
  evaluation?: Evaluation | null;
  // 실게시(affiliateReels) 기반 — 데이터 있을 때만. draft(제출)와 별개.
  effectiveWeek?: number;        // 실게시 누적 기반 유효주차(달력주차 대신 표시)
  calendarWeek?: number;         // 원래 달력주차(참고)
  actualPosts?: number;          // 그날 실제 IG 게시 수
  actualCum?: number;            // 누적 실게시 수
  behind?: boolean;              // 목표 누적에 뒤처짐
  postStatus?: 'warmup' | 'ontrack' | 'behind' | 'future';
  posts?: PostLink[];            // 그날 실게시 링크
  delayMarker?: DelayMarker | null;  // 지연 구간 끝 행에 부착
  isWeekStart?: boolean;         // 실활동 주차가 바뀌는 첫 행 → 지급 기준 구분행
  weekCriteria?: WeekCriteria | null;  // 그 주차 지급 달성 기준
  weekProgress?: WeekProgress | null;  // 그 주차 게이지(현재/목표)
  baselineIncomplete?: boolean;  // baseline 이전/경계 주차 → 과거 실측 부족(개수 표시 참고용)
  weekClosed?: boolean;          // 지급 완료·마감된 주차(주 단위 마감) → "settled & closed"
  // 경계일 분할: 그날 게시가 두 주차에 걸쳐 같은 날짜가 두 행으로 나뉜 경우.
  //   posts는 그 행 몫의 릴만 담긴다(백엔드에서 시간순으로 배분).
  splitOfDay?: { week: number; posts: number; total: number } | null;
}
interface RetainerPayout { id: string; week: number | null; amount: number; date: string; note: string; sourceAccount?: string; }
interface BonusPayout { id: string; date: string; amount: number; note: string; }
interface SchedulerData {
  handle: string; d0Date: string | null; schedulerUrl: string;
  schedule: ScheduleRow[]; drafts: DraftSlot[];
  payouts: { retainer: RetainerPayout[]; bonus: BonusPayout[] };
  payoutCriteria?: WeekCriteria[];   // W1/W2/W3+ 지급 기준 범례
  warmupReports?: number;            // 제출된 웜업 리포트 수(W1 기준=7)
  firstDraftReviewedAt?: string | null;   // 첫 초안 검토 완료 도장(희윤)
  firstDraftReviewedBy?: string | null;
}

const PHASE_LABEL: Record<string, string> = { create: 'Create acct', warmup: 'Warm-up', ramp: 'Ramp-up', full: 'Full Upload' };
// 하루 게시 카운트 상한(백엔드 computeRetainerPending의 DAILY_CAP과 일치). 24시간 경계 오차를
// 흡수해 3까지 인정하고, 초과분(4번째~)은 지급 판정에서 빠지므로 UI에서 투명 처리 + 경고로 표시.
const DAILY_CAP = 3;
const fmtUSD = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = (iso: string | null) => iso ? new Date(iso).toISOString().slice(5, 10).replace('-', '/') : 'TBD';

const SchedulerTab: React.FC<{ handle: string }> = ({ handle }) => {
  const [data, setData] = useState<SchedulerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 열려 있는 초안 슬롯의 상세 패널 키("day-slot"). null이면 닫힘.
  const [openSlot, setOpenSlot] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null); // 업로드 중인 슬롯 키

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ugc/${encodeURIComponent(handle)}/scheduler`);
      const json = await res.json();
      if (json.success) { setData(json.data); setError(null); }
      else setError(json.error || 'Failed to load scheduler');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [handle]);

  useEffect(() => { load(); }, [load]);

  // 초안 상세 모달: ESC로 닫기 + 열려 있는 동안 배경 스크롤 잠금.
  useEffect(() => {
    if (!openSlot) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenSlot(null); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [openSlot]);

  // (day,slot) → 초안 슬롯 룩업.
  const draftFor = (day: number, slot: number): DraftSlot | undefined =>
    data?.drafts.find((d) => d.day === day && d.slot === slot);

  // retainer·bonus 모두 지급 '날짜'로 그 날 행에만 표시(주 전체 아님). week 매칭은 계정 전환 시
  // 옛/새 계정의 같은 week가 충돌해 반복 표시되므로 쓰지 않는다.
  const retainerFor = (dateIso: string | null): RetainerPayout | undefined => {
    if (!dateIso) return undefined;
    const d = dateIso.slice(0, 10);
    return data?.payouts.retainer.find((p) => (p.date || '').slice(0, 10) === d);
  };
  // 계정 전환자(sourceAccount 2개+)면 payout 셀에 어느 계정·주차인지 표시.
  const multiAccount = new Set((data?.payouts.retainer || []).map((p) => (p.sourceAccount || '').toLowerCase()).filter(Boolean)).size >= 2;
  const bonusFor = (dateIso: string | null): BonusPayout | undefined => {
    if (!dateIso) return undefined;
    const d = dateIso.slice(0, 10);
    return data?.payouts.bonus.find((p) => (p.date || '').slice(0, 10) === d);
  };

  const uploadDraft = async (day: number, slot: number, file: File) => {
    // 서버(multer) 한도와 같은 값. 넘으면 서버가 HTML 500을 돌려줘 res.json()이 깨지고, 크리에이터는
    // 알아볼 수 없는 오류만 보고 "올렸다"고 믿는다(2026-10-23 harry 4K 원본 두 번 거부) → 올리기 전에 막는다.
    const MAX_MB = 500;
    if (file.size > MAX_MB * 1024 * 1024) {
      alert(`This file is ${Math.round(file.size / 1024 / 1024)} MB, over the ${MAX_MB} MB limit. Please export it at 1080p (H.264, MP4) and upload again.`);
      return;
    }
    const key = `${day}-${slot}`;
    setUploading(key);
    try {
      const form = new FormData();
      form.append('video', file);
      form.append('day', String(day));
      form.append('slot', String(slot));
      const res = await fetch(`${API_URL}/api/ugc/${encodeURIComponent(handle)}/draft`, { method: 'POST', body: form });
      const json = await res.json().catch(() => ({ success: false, error: `Upload failed (HTTP ${res.status}). Please try again, or send the video to your manager on Slack.` }));
      if (json.success) {
        // 최신 초안 목록만 갱신(그리드/payout은 그대로).
        setData((prev) => prev ? { ...prev, drafts: json.data.drafts } : prev);
      } else {
        alert(json.error || 'Upload failed');
      }
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setUploading(null);
    }
  };

  if (loading) return <div className="sched__msg">Loading scheduler…</div>;
  if (error) return <div className="sched__msg sched__msg--err">{error}</div>;
  if (!data) return null;
  // d0Date 없어도 5주 프리뷰 그리드를 보여준다(웜업→램프업→포스팅 흐름 이해용). 날짜는 TBD.
  const noD0 = !data.d0Date;

  const todayRow = data.schedule.find((r) => r.isToday);
  // 실게시 데이터가 있으면 실제 게시 누적을, 없으면 스케줄 목표 누적을 표시.
  const hasPostData = data.schedule.some((r) => typeof r.actualPosts === 'number');
  const totalPosted = data.schedule.reduce((s, r) => s + (r.actualPosts || 0), 0);
  const totalGoal = data.schedule.reduce((s, r) => s + r.uploadCount, 0);

  return (
    <div className="sched">
      <div className="sched__kpis">
        <div className="sched__kpi">
          <span className="l">Now</span>
          <span className="v">
            {todayRow ? `W${todayRow.week} · Day ${todayRow.day}` : '—'}
          </span>
        </div>
        <div className="sched__kpi">
          <span className="l">{hasPostData ? 'Posted' : 'Uploaded'}</span>
          <span className="v">
            {hasPostData ? totalPosted : totalGoal} <small>{hasPostData ? 'live' : 'vids'}</small>
          </span>
        </div>
        <div className="sched__kpi"><span className="l">Phase</span><span className="v">{todayRow ? PHASE_LABEL[todayRow.phase] : '—'}</span></div>
        {data.schedulerUrl && <a className="sched__sheet" href={data.schedulerUrl} target="_blank" rel="noreferrer">Legacy sheet ↗</a>}
      </div>

      {noD0 && (
        <div className="sched__preview-banner">
          📋 This is a preview of the full 5-week schedule. Once your <b>start date (Day 0)</b> is set, real dates fill in and
          you can upload drafts into each slot. It shows how the project flows: <b>Warm-up (week 1)</b> → <b>Ramp-up (days 8–10)</b> →
          <b>Full posting (2/day)</b>.
        </div>
      )}

      <p className="sched__hint">
        Every draft cell is a dropzone — drag a video onto it or click to select. Re-uploading stacks a new version (v1→v2…);
        the latest is live. Retainer / Bonus columns are read-only (entered on the Payouts tab).
      </p>
      {hasPostData && (
        <p className="sched__hint">
          🎬 <b>Posted</b> shows the reels actually live on Instagram that day (separate from your uploaded drafts). Each week's payout
          unlocks once you reach its post goal — the <b>Week</b> gauge tracks your progress. Go at your own pace: a week stays open until
          its goal is met, and the next week simply begins after that. <b>Please upload 2 posts a day.</b> If you upload more than that in a
          single day, the extra posts won't be counted toward your week — so spread your posts across the days.

        </p>
      )}
      {data.payoutCriteria && data.payoutCriteria.length > 0 && (
        <div className="sched__criteria">
          <span className="sched__criteria-hd">💵 Payout criteria per week</span>
          <ul>
            {data.payoutCriteria.map((c) => (
              <li key={c.week}>
                <b>{c.week === 3 ? 'W3+' : `W${c.week}`}</b>
                <span className="sched__criteria-t">{c.kind === 'warmup_report' ? `${c.target} reports` : `${c.target} posts`}</span>
                <span className="sched__criteria-l">{c.label}</span>
                {c.week === 1 && typeof data.warmupReports === 'number' && (
                  <span className={`sched__criteria-prog${data.warmupReports >= c.target ? ' met' : ''}`}>
                    {data.warmupReports}/{c.target}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {/* 지급 "시점"을 안 적어두면 크리에이터가 계속 물어본다(2026-09-02 실제 문의: "일요일 몇 시에
              주나요?"). 일요일 일괄 → 목표 달성 즉시로 바뀐 정책과, 보너스가 리테이너와 함께 나간다는
              사실을 여기 명시한다. 웜업 단계에서도 보이도록 payoutCriteria 블록에 둔다(게시 데이터가
              있어야 뜨는 위 문단에 넣으면 W1 크리에이터는 못 본다). */}
          <p className="sched__criteria-when">
            Payouts aren't tied to a fixed day of the week. The moment you hit a week's goal, that week's
            retainer goes out <b>the same day</b> and reaches your account in 1–2 business days. Any referral
            bonus you've earned but not yet been paid is sent <b>in the same payout</b>.
          </p>
        </div>
      )}
      <p className="sched__hint sched__hint--posting">
        ⏰ <b>How to structure your week:</b> we want each week to be <b>14 videos, spread as 2 per day across 7 days</b>. Aim to post within
        <b> 5&nbsp;PM–11&nbsp;PM local time</b>, keeping the two daily posts <b>at least 2 hours apart</b>.
        <br /><br />
        We've found that dumping <b>3+ posts in a single day doesn't help you go viral</b>, so this system counts <b>at most 2 posts per day</b>
        toward your week <b>(up to 3 allowed, to cover timezone edge cases)</b>. You're free to post more than that at your own discretion to chase
        bonuses, but anything beyond that <b>won't be counted</b> toward unlocking your weekly payout.
      </p>

      <div className="sched__wrap">
        <table className="sched__tbl">
          <thead>
            <tr>
              <th>Date</th><th>Week</th><th>Day</th><th>Phase</th><th>Daily Goal</th><th>Upload</th><th>Cumul.</th>
              <th>Draft 1 (evening)</th><th>Draft 2 (evening, 2h+ later)</th><th>Posted ↗</th><th>Retainer</th><th>Bonus</th>
            </tr>
          </thead>
          <tbody>
            {data.schedule.map((r) => {
              const ret = retainerFor(r.date);
              const bon = bonusFor(r.date);
              return (
                <React.Fragment key={r.day}>
                {r.isWeekStart && r.weekCriteria && (
                  <tr className="sched__wkhead">
                    <td colSpan={12}>
                      <div className="sched__wkhead-row">
                        <span className="sched__wkhead-title">
                          <b>Week {r.week}</b>
                          <span className="sched__wkhead-l">
                            {r.weekCriteria.kind === 'warmup_report'
                              ? ` — ${r.weekCriteria.target} warm-up reports`
                              : ` — ${r.weekCriteria.target} posts to unlock payout`}
                          </span>
                          {r.weekCriteria.kind === 'posts' && (
                            <span className="sched__wkhead-cap" title={'Please upload 2 posts/day. If you upload more than that, it will not be counted.'}>
                              {' '}· Please upload 2 posts/day (extra posts won't be counted)
                            </span>
                          )}
                        </span>
                        {r.weekProgress && (() => {
                          const wp = r.weekProgress;
                          const done = wp.current >= wp.target;
                          return (
                            <span className="sched__gauge" title={`${wp.current} / ${wp.target} ${wp.kind}`}>
                              <span className="sched__gauge-cells">
                                {Array.from({ length: wp.target }).map((_, i) => (
                                  <span key={i} className={`sched__gauge-cell${i < wp.current ? ' filled' : ''}`} />
                                ))}
                              </span>
                              <span className={`sched__gauge-num${done ? ' done' : ''}`}>
                                {wp.current}/{wp.target}{done ? ' ✓' : ''}
                              </span>
                            </span>
                          );
                        })()}
                        {r.weekClosed ? (
                          <span
                            className="sched__wkhead-note sched__wkhead-closed"
                            title={'This week is settled and closed — your payout for it has already been sent. Counting for your next payout starts fresh from your posts after your last payout, so the numbers here are just for reference.'}
                          >
                            {' '}· 🔒 settled &amp; closed (paid)
                          </span>
                        ) : r.baselineIncomplete && (
                          <span
                            className="sched__wkhead-note"
                            title={'This week predates our detailed post tracking. Some of your earlier posts were counted in bulk at the point of your last payout, so the per-day counts shown here may look incomplete. Your payout for this week was already settled correctly based on your total posts.'}
                          >
                            {' '}· ⓘ earlier week — per-day counts are for reference only (payout already settled)
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                <tr className={`${r.isToday ? 'sched__today' : ''}${r.splitOfDay ? ' sched__split' : ''}`.trim()}>
                  <td className="m">
                    {fmtDate(r.date)}
                    {r.splitOfDay && (
                      <span
                        className="sched__split-info"
                        title={`This day is split across two weeks. You posted ${r.splitOfDay.total} on ${fmtDate(r.date)}, and ${r.splitOfDay.posts} of them ${r.splitOfDay.posts === 1 ? 'belongs' : 'belong'} to Week ${r.splitOfDay.week}. A week closes the moment you hit its target, so the rest of that day counts toward the next week.`}
                        aria-label="Why this date appears twice"
                      >i</span>
                    )}
                  </td>
                  <td className="m">{r.week || '—'}</td>
                  <td className="m">{r.day}</td>
                  <td><span className={`sched__ph sched__ph--${r.phase}`}>{PHASE_LABEL[r.phase]}</span></td>
                  <td className="goal">{r.dailyGoal}</td>
                  <td className="m">{r.phase === 'create' || r.phase === 'warmup' ? '-' : r.uploadCount}</td>
                  <td className="m">{r.phase === 'create' || r.phase === 'warmup' ? '-' : r.cumulativeTotal}</td>
                  {[1, 2].map((slot) => {
                    // create/warmup 행은 업로드 슬롯 없음. ramp는 slot1만.
                    const noSlot = r.phase === 'create' || r.phase === 'warmup' || (r.phase === 'ramp' && slot === 2);
                    if (noSlot) return <td key={slot} className="empty">—</td>;
                    return (
                      <DraftCell
                        key={slot}
                        day={r.day}
                        slot={slot}
                        draft={draftFor(r.day, slot)}
                        uploading={uploading === `${r.day}-${slot}`}
                        opened={openSlot === `${r.day}-${slot}`}
                        onToggle={() => setOpenSlot(openSlot === `${r.day}-${slot}` ? null : `${r.day}-${slot}`)}
                        onUpload={(file) => uploadDraft(r.day, slot, file)}
                      />
                    );
                  })}
                  {/* 실게시(Posted): 그날 실제 IG에 나간 릴. draft(제출)와 별개. 뒤처짐 표시 없음 —
                      본인 누적 성과대로 진행하고 목표를 다 채워야 지급될 뿐(휴가·병가 등 사정 무관). */}
                  <td className="sched__posted">
                    {r.posts && r.posts.length > 0 ? (() => {
                      // POV·untapped 캡션 릴은 2개로 카운트(payoutEngine.reelWeight와 동일 규칙, isPov=가중2) — 일일 카운트·cap
                      // 초과 판정 모두 가중 누적 기준으로 계산해야 게이지·지급 판정과 일치한다.
                      let cum = 0;
                      const weighted = r.posts.reduce((s, p) => s + (p.isPov ? 2 : 1), 0);
                      return (
                        <span className="sched__posted-ok">
                          {r.posts.map((p, i) => {
                            const w = p.isPov ? 2 : 1;
                            const over = cum >= DAILY_CAP;
                            cum += w;
                            const code = p.code;
                            const label = `${p.playCount.toLocaleString()} views${p.isPov ? ' · POV/untapped — counts as 2 posts' : ''}${over ? ` · not counted (only ${DAILY_CAP}/day count toward payout)` : ''}`;
                            const inner = <>🎬{p.isPov && <sup className="sched__pov-badge">×2</sup>}</>;
                            return code
                              ? <a key={code} className={over ? 'sched__reel-over' : ''} href={`https://instagram.com/reel/${code}`} target="_blank" rel="noreferrer" title={label}>{inner}</a>
                              : <span key={i} className={over ? 'sched__reel-over' : ''} title={label}>{inner}</span>;
                          })}
                          <span className="sched__posted-n">{Math.min(weighted, DAILY_CAP)}</span>
                          {weighted > DAILY_CAP && (
                            <span className="sched__posted-warn" title={`Counts ${weighted} today (POV and "untapped" posts count as 2) — only ${DAILY_CAP} count toward payout. Extra posts don't advance your week.`}>❗</span>
                          )}
                        </span>
                      );
                    })() : <span className="empty">—</span>}
                  </td>
                  <td className="pay">
                    {ret ? (
                      <span title={multiAccount && ret.sourceAccount ? `W${ret.week} @${ret.sourceAccount}` : (ret.week ? `W${ret.week}` : '')}>
                        {fmtUSD(ret.amount)}
                        {multiAccount && ret.sourceAccount && (
                          <span style={{ display: 'block', fontSize: '0.72em', color: '#888' }}>W{ret.week} @{ret.sourceAccount}</span>
                        )}
                      </span>
                    ) : <span className="empty">—</span>}
                  </td>
                  <td className="pay">{bon ? fmtUSD(bon.amount) : <span className="empty">—</span>}</td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {openSlot && (() => {
        const [d, s] = openSlot.split('-').map(Number);
        const draft = draftFor(d, s);
        return (
          <div className="sched__overlay" onClick={() => setOpenSlot(null)}>
          <div className="sched__detail" onClick={(e) => e.stopPropagation()}>
            <div className="sched__detail-head">
              Day {d} · Draft {s} {s === 1 ? '(5pm)' : '(9pm)'}
              <button className="sched__x" onClick={() => setOpenSlot(null)}>×</button>
            </div>
            <div className="sched__detail-body">
              <DropZone uploading={uploading === openSlot} onUpload={(file) => uploadDraft(d, s, file)} />
              <div className="sched__ver">
                <div className="sched__ver-head">Version history</div>
                {draft && draft.versions.length > 0 ? (
                  <div className="sched__tl">
                    {[...draft.versions].reverse().map((v) => (
                      <div className="sched__ev" key={v.version}>
                        <div className="who">v{v.version}{v.version === draft.versions.length ? <span className="cur"> · current</span> : null}</div>
                        <div className="c">
                          {v.url
                            ? <a className="file" href={v.url} target="_blank" rel="noreferrer">{v.filename || 'video'}</a>
                            : <span className="file">{v.filename || 'video'}</span>}
                          {v.uploadedAt ? <span className="ts"> · {new Date(v.uploadedAt).toLocaleString()}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="sched__ver-empty">No uploads yet.</div>}
              </div>
            </div>
          </div>
          </div>
        );
      })()}
    </div>
  );
};

/* 초안 셀 — 채워짐(버전 pill)/빈칸(드롭 힌트) 모두 드래그드롭·클릭 업로드 가능. */
const DraftCell: React.FC<{
  day: number; slot: number; draft?: DraftSlot; uploading: boolean; opened: boolean;
  onToggle: () => void; onUpload: (file: File) => void;
}> = ({ draft, uploading, opened, onToggle, onUpload }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const current = draft?.current;

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onUpload(file);
  };
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = '';
  };

  return (
    <td
      className={`sched__slot${dragOver ? ' sched__slot--drag' : ''}${opened ? ' sched__slot--open' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm" hidden onChange={pick} />
      {uploading ? (
        <span className="sched__slot-up">Uploading…</span>
      ) : current ? (
        <button className="sched__pill" onClick={onToggle} title="Open / add version">
          <span className="v">v{current.version}</span>
          <span className="fn">{current.filename || 'video'}</span>
        </button>
      ) : (
        <button className="sched__drop" onClick={() => inputRef.current?.click()}>⬆ Drop / select</button>
      )}
    </td>
  );
};

/* 상세 패널의 큰 드롭존. */
const DropZone: React.FC<{ uploading: boolean; onUpload: (file: File) => void }> = ({ uploading, onUpload }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className={`sched__dz${dragOver ? ' sched__dz--drag' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) onUpload(f); }}
      onClick={() => inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm" hidden
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
      <div className="ic">⬆️</div>
      <div className="t">{uploading ? 'Uploading…' : 'Drag & drop your video'}</div>
      <div className="d">MP4 / MOV / WebM · uploads to R2 · each upload = a new version</div>
    </div>
  );
};

export default SchedulerTab;
